import React from 'react'

import { Anchor, Text, Tooltip } from '@mantine/core'
import classname from 'classnames'
import { observer } from 'mobx-react-lite'
import { isMacOs } from 'react-device-detect'
import { useDrag } from 'react-dnd'

import { ScriptStatus, ScriptStatusMap } from '@showrunner/codex'

import { ScriptStatusIcon } from '#components/FaIcon'
import {
  RundownListingMenu,
  ScriptListingMenu,
} from '#components/FolderExplorer/DotDotDotMenu'
import { NameListingInput } from '#components/FolderExplorer/NameListingInput'
import {
  useFormattedTimestamp,
  useNavigation,
  useScriptMultiClick,
} from '#hooks'
import { useMst } from '#state'
import { IListing, isScriptListing } from '#state/types'

import styles from './DocumentListing.module.css'

type DocumentListingProps = {
  id: string
  href: string
  icon: string
  title: string
  subtitle: string
  selected: boolean
  isDraggable: boolean
  menu: React.ReactNode
  status?: ScriptStatus
  onClick: (e: React.MouseEvent) => void
  hoverTooltip?: string
}

export function DocumentListingBase(props: DocumentListingProps) {
  const {
    icon,
    selected,
    title,
    subtitle,
    menu,
    href,
    onClick,
    isDraggable,
    id,
    status,
    hoverTooltip,
  } = props

  const miniBadge =
    status && status !== ScriptStatusMap.OPEN ? (
      <ScriptStatusIcon status={status} size="xs" />
    ) : null

  const linkRef = React.useRef<HTMLAnchorElement>(null)
  useScriptMultiClick({
    ref: linkRef,
    onClick,
  })

  const draggableRef = React.useRef<HTMLLIElement>(null)
  const [{ isDragging }, connectDraggable] = useDrag(
    () => ({
      canDrag: isDraggable,
      type: 'script',
      item: { id },
      collect: (monitor) => ({
        isDragging: monitor.isDragging(),
      }),
    }),
    [id, isDraggable],
  )
  // oxlint-disable-next-line react/refs
  connectDraggable(draggableRef)

  return (
    <li
      ref={draggableRef}
      className={classname(styles.root, {
        [styles.selected]: selected,
      })}
      data-doc-listing={id}
      data-draggable={isDraggable || undefined}
      data-dragging={isDragging || undefined}
    >
      <Anchor
        className={styles.link}
        color="dimmed"
        ref={linkRef}
        href={href}
        onClick={(e: React.MouseEvent<HTMLAnchorElement>) => {
          // prevent default for normal clicks to avoid reloading the page.
          const modifiedClick = isMacOs ? e.metaKey : e.ctrlKey
          if (!modifiedClick) e.preventDefault()
        }}
      >
        <i className={classname(icon, styles.icon)} />
        <Tooltip
          disabled={!hoverTooltip}
          openDelay={500}
          label={hoverTooltip}
          withinPortal
          position="bottom-start"
        >
          <Text className={styles.title} truncate fz={15}>
            {title}
          </Text>
        </Tooltip>
        {miniBadge}
        {subtitle && (
          <Text className={styles.subtitle} c="dimmed" fz={14} truncate>
            {subtitle}
          </Text>
        )}
      </Anchor>
      <div className={styles.menu}>{menu}</div>
    </li>
  )
}

export const DocumentListing = observer(function DocumentListing({
  listing,
}: {
  listing: IListing
}) {
  const { user, location, getFolderPath, view } = useMst()
  const { navigateToScript, navigateToRundown, pathToScript, pathToRundown } =
    useNavigation()
  const openRundownId = location.getPathParam('rundownId')
  const tooltip = view.selectedFolderIsCollection
    ? getFolderPath(listing.folderId)
        .map(({ displayName }) => displayName)
        .join(' > ')
    : undefined

  // let shared scripts be dragged into rundowns if org has the feature and user has permission
  const isValidScriptForRundown =
    openRundownId &&
    user.canEditRundowns &&
    isScriptListing(listing) &&
    !listing.isPrivate

  const { name, contentsModifiedAt, lastModifier, parentFolder } = listing

  const formattedTimestamp = useFormattedTimestamp({
    date: contentsModifiedAt,
    user: lastModifier?.name,
  }).replace('Last edited by ', '')

  const parentFolderName =
    parentFolder && !parentFolder.isRootFolder ? parentFolder.name : ''

  // we don't show lastEdit timestamp in My history because we sort by recent opens
  const subtitle = view.selectedFolderIsMyHistory
    ? parentFolderName
    : formattedTimestamp

  const icon = isScriptListing(listing)
    ? `fas ${listing.icon}`
    : 'fas fa-table-cells'

  const menu = isScriptListing(listing) ? (
    <ScriptListingMenu scriptListing={listing} />
  ) : (
    <RundownListingMenu rundownListing={listing} />
  )

  const href = isScriptListing(listing)
    ? pathToScript(listing.id)
    : pathToRundown(String(listing.id))

  const handleClick = (e: React.MouseEvent) => {
    // if a modifier key is detected, let the browser do its thing
    if (isMacOs ? e.metaKey : e.ctrlKey) return

    if (isScriptListing(listing)) {
      navigateToScript(listing.id, undefined, { source: 'sidebar' })
    } else {
      navigateToRundown(String(listing.id), { source: 'sidebar' })
    }
  }

  if (listing.isEditingName) {
    return (
      <div className={styles.root}>
        <NameListingInput
          initialValue={listing.name}
          mode={'rename'}
          itemType={isScriptListing(listing) ? 'script' : 'rundown'}
          onCancel={() => listing.setIsEditingName(false)}
          onSubmit={async ({ name }) => {
            try {
              await listing.rename(name)
            } catch (e) {
              listing.setIsEditingName(false)
            }
          }}
          icon={icon}
        />
      </div>
    )
  } else {
    return (
      <DocumentListingBase
        id={String(listing.id)}
        isDraggable={!!isValidScriptForRundown}
        onClick={handleClick}
        title={name}
        subtitle={subtitle}
        selected={listing.isSelected}
        icon={icon}
        menu={menu}
        href={href}
        status={isScriptListing(listing) ? listing.accessLevel : undefined}
        hoverTooltip={tooltip}
      />
    )
  }
})
